// World-only workspace flows: the world bundle, draft proposals, and
// publishing. The generic workspace layer (entities, workspaces,
// conversations, mappers) lives in creator-workspaces.ts.
import { supabase, uid, isRecord, toRecord, toRecordArray, toNullableNumber, toNullableString } from './shared'
import { mapWorldShellRow, mapWorldVersionRow } from './worlds'
import {
  mapDraftProposal,
  mapDraftRevision,
  mapDraftWorkspace,
  mapEditableEntity,
  mapWorldWorkspaceBundle,
  mapWorkspaceEvent,
} from './creator-workspaces'
import type { WorldWorkspaceBundle } from '@/types/domain'
import type {
  WorldDraftProposalApproveRequest,
  WorldDraftProposalApproveResponse,
  WorldDraftProposalCreateRequest,
  WorldDraftProposalCreateResponse,
  WorldDraftProposalRejectRequest,
  WorldDraftProposalRejectResponse,
  WorldWorkspaceEnsureRequest,
  WorldWorkspacePublishRequest,
  WorldWorkspacePublishResponse,
} from '@/types/api'

export async function getWorldWorkspace(
  worldId: number | string,
  selectedConversationId?: number | string | null,
): Promise<WorldWorkspaceBundle> {
  const userId = await uid()
  const { data: entityRow, error: entityError } = await supabase
    .from('editable_entities')
    .select('id')
    .eq('entity_type', 'world')
    .eq('entity_id', Number(worldId))
    .eq('owner_id', userId)
    .maybeSingle()

  if (entityError) throw entityError
  if (!entityRow || !isRecord(entityRow)) {
    return {
      editable_entity: null,
      workspace: null,
      conversations: [],
      conversation: null,
      messages: [],
      events: [],
      revisions: [],
      proposals: [],
      active_version: null,
      world_versions: [],
    }
  }

  const { data: workspaceRow, error: workspaceError } = await supabase
    .from('draft_workspaces')
    .select('id')
    .eq('editable_entity_id', entityRow.id)
    .eq('status', 'active')
    .maybeSingle()

  if (workspaceError) throw workspaceError
  if (!workspaceRow || !isRecord(workspaceRow)) return ensureWorldWorkspace(worldId)

  const { data, error } = await supabase.rpc('world_workspace_bundle', {
    p_world_id: Number(worldId),
    p_workspace_id: Number(workspaceRow.id),
    p_selected_conversation_id: toNullableNumber(selectedConversationId),
  })
  if (error) throw error
  return mapWorldWorkspaceBundle(data)
}

export async function ensureWorldWorkspace(
  worldId: number | string,
  request: WorldWorkspaceEnsureRequest = {},
): Promise<WorldWorkspaceBundle> {
  const { data, error } = await supabase.rpc('rpc_ensure_world_workspace_v1', {
    p_world_id: Number(worldId),
    p_seed_version_id: request.seed_version_id ?? null,
  })
  if (error) throw error
  return mapWorldWorkspaceBundle(data)
}

export async function createWorldDraftProposal(
  request: WorldDraftProposalCreateRequest,
): Promise<WorldDraftProposalCreateResponse> {
  const { data, error } = await supabase.rpc('rpc_create_world_draft_proposal_v1', {
    p_workspace_id: Number(request.workspace_id),
    p_conversation_id: Number(request.conversation_id),
    p_turn_id: toNullableString(request.turn_id),
    p_base_revision: Number(request.base_revision),
    p_operations: request.operations,
    p_metadata: request.metadata ?? {},
  })
  if (error) throw error
  const payload = toRecord(data)
  return {
    proposal: mapDraftProposal(payload.proposal),
    events: toRecordArray(payload.events).map(mapWorkspaceEvent),
  }
}

export async function approveWorldDraftProposal(
  proposalId: number | string,
  request: WorldDraftProposalApproveRequest,
): Promise<WorldDraftProposalApproveResponse> {
  const { data, error } = await supabase.rpc('rpc_approve_world_draft_proposal_v1', {
    p_proposal_id: Number(proposalId),
    p_expected_base_revision: Number(request.expected_base_revision),
  })
  if (error) throw error
  const payload = toRecord(data)
  return {
    workspace: mapDraftWorkspace(payload.workspace)!,
    revision: mapDraftRevision(payload.revision),
    proposal: mapDraftProposal(payload.proposal),
    events: toRecordArray(payload.events).map(mapWorkspaceEvent),
  }
}

export async function rejectWorldDraftProposal(
  proposalId: number | string,
  request: WorldDraftProposalRejectRequest = {},
): Promise<WorldDraftProposalRejectResponse> {
  const { data, error } = await supabase.rpc('rpc_reject_world_draft_proposal_v1', {
    p_proposal_id: Number(proposalId),
    p_reason: request.reason ?? '',
  })
  if (error) throw error
  const payload = toRecord(data)
  return {
    proposal: mapDraftProposal(payload.proposal),
    events: toRecordArray(payload.events).map(mapWorkspaceEvent),
  }
}

export async function publishWorldWorkspace(
  workspaceId: number | string,
  request: WorldWorkspacePublishRequest,
): Promise<WorldWorkspacePublishResponse> {
  const { data, error } = await supabase.rpc('rpc_publish_world_workspace_v2', {
    p_workspace_id: Number(workspaceId),
    p_expected_revision: Number(request.expected_revision),
  })
  if (error) throw error
  const payload = toRecord(data)
  return {
    editable_entity: mapEditableEntity(payload.editable_entity)!,
    world: mapWorldShellRow(payload.world),
    active_version: mapWorldVersionRow(payload.active_version),
    workspace: mapDraftWorkspace(payload.workspace)!,
  }
}
